import {
  useCallback, useEffect, useRef, useState, type CSSProperties, type JSX, type ReactNode,
} from 'react'
import {
  api, ApiError,
  type BaselineAdjustment, type BaselineDerivation, type ConfidenceLabel, type ConfigDerivation,
  type DerivationAspect, type DerivationByAspect, type EstimateFlag, type FactorsDerivation,
  type SustainDerivation,
} from '../api.js'
import { asWords, formatDate, formatHour, formatMoney, grouped, KwFigure } from './Pill.js'
import { T } from '../tokens.js'

/**
 * The derivation accordion — display rule 5, *"every number has a reachable
 * derivation. Not visible by default — reachable."*
 *
 * ## "Reachable, not resident" is a data-loading instruction, not only a layout one
 *
 * **Each aspect is fetched when its row is opened, never on mount.** Four
 * derivations fetched eagerly for every account would be four requests nobody
 * asked for, on the screen a rep opens most, and three of them thrown away. One
 * row is open at a time.
 *
 * The fetch is issued from the click handler rather than from an effect keyed on
 * the open row, and that is deliberate: `main.tsx` mounts the app in
 * `StrictMode`, which double-invokes effects in development, so an effect here
 * would fire two identical requests per open and the network tab would show
 * exactly the behaviour this component exists to avoid.
 *
 * **An aspect already fetched is not fetched again**, and its summary value stays
 * on the row header after the row is closed. Reaching for a derivation once is
 * the cost; re-reading what you already reached for is not another one.
 *
 * ## The baseline copy departs from the mock, and it is a correctness fix
 *
 * The mock's baseline row is labelled *"Baseline — 10-in-10 comparable days,
 * day-of adjustment capped at 20%"* and its body says the hourly average is
 * *"then adjusted on the day of the event using the 2pm–4pm ratio and capped at
 * 20%"*. **That is false of every estimate this pilot produces.** The day-of
 * adjustment is a *settlement-time* correction: it measures a customer's actual
 * pre-window load on the day of a *called event* and adjusts the baseline before
 * they are paid. This pilot computes forward-looking targeting estimates whose
 * event day has not happened and therefore has no readings, so
 * `computeBaseline`'s adjustment never fires — measured, `adjustmentFactor === 1`
 * on 313 of 313 accounts.
 *
 * A rep reading this accordion aloud to a facility engineer is the exact person
 * who must not be told a correction ran when none did, so the copy says which of
 * the two it is rather than being softened into "may be adjusted". The cap is
 * still reported, because it is part of the method the figure was produced
 * under, and it is read from the payload rather than written here — see
 * `baselineBody`.
 *
 * The other three rows' labels and bodies are the mock's, transcribed. Their
 * **values** are not: the mock hardcodes `rev 7 · effective 1 Jun 2026`, and the
 * configuration a figure was computed under is the one thing on this screen that
 * must never be a literal in a browser.
 *
 * ## Accessibility
 *
 * A real disclosure pattern: one `<button aria-expanded>` per row, each naming
 * its panel through `aria-controls`, each panel naming its button through
 * `aria-labelledby`. The mock hangs click handlers on `<div>`s — unreachable by
 * keyboard, silent to a screen reader. The panel element is always in the
 * document and carries `hidden` when closed, so `aria-controls` never points at
 * an element that is not there.
 */

const HEADING = 'Derivation — reachable, not resident'

/**
 * The heading while a what-if is on the page.
 *
 * It replaces the promise ("reachable, not resident") with the one thing a rep
 * has to know before opening a row under a scenario: these four rows are the
 * working behind the *record*, not behind the figures the scenario put on the
 * page beside them.
 */
const SCENARIO_HEADING = 'Derivation — of the estimate of record, not of this scenario'

/**
 * The heading while a figure recomputed for another day is on the page.
 *
 * Same failure as the scenario one and a different sentence, because it is a
 * different fact: `GET /api/accounts/:id/derivation/:aspect` reads the *stored*
 * estimate, so under `?asOf` these rows describe a day the rep is no longer
 * looking at. An open `Baseline — 10-in-10 comparable days` panel listing July
 * days beside a September nomination is read as the working behind the
 * September figure.
 */
const AS_OF_HEADING = 'Derivation — of the estimate of record, not of this date'

/**
 * **Not the mock's label.** The mock's says "day-of adjustment capped at 20%",
 * which claims a correction that did not run. See the module docblock.
 */
const BASELINE_LABEL = 'Baseline — 10-in-10 comparable days'
const FACTORS_LABEL = 'Curtailment factors applied'
const SUSTAIN_LABEL = 'Hours that must be sustained'
const CONFIG_LABEL = 'Configuration in effect'

const FACTORS_BODY = 'Cooling, base and schedule factors are keyed on industry code and building '
  + 'type. Where no building type is recorded the industry-only row governs, which is why an '
  + 'uncharacterized account still scores rather than being excluded.'

/**
 * The mock's, transcribed. Note that `5pm` and `9pm` are the seeded event
 * window's own first and last hours, written into prose — correct today and the
 * same class of literal as the `rev 7` this file refuses one row down. Carried
 * as a finding rather than fixed, because the brief mandates this sentence.
 */
const SUSTAIN_BODY = 'The nomination is the weakest hourly value across the hours the customer '
  + 'must sustain. Clearing the number at 5pm and missing it at 9pm is a shortfall, so the window '
  + 'average is never the commitment.'

/**
 * The mock says "as published in revision 7". `rev 7` is the mock's stand-in for
 * a version id, so the real id goes here for the same reason it goes in the
 * row's value.
 */
function configBody(configVersionId: string): string {
  return `Rates, season dates, baseline method and curtailment factors as published in `
    + `${configVersionId}. Earlier revisions are retained, so any historical estimate can be `
    + `reproduced exactly as it stood.`
}

/**
 * The corrected baseline body.
 *
 * The cap is interpolated from the payload rather than written as `20%`. On the
 * shipped configuration that renders byte-identical to the mandated sentence;
 * under a configuration with a different cap it stays true, which a literal
 * would not — the same reason the event window is on the wire rather than in
 * this package. The `enabled: false` branch exists because saying "configured at
 * 20% applies at settlement" of a switched-off adjustment would be the mock's
 * mistake in the other direction.
 */
function baselineBody(adjustment: BaselineAdjustment): string {
  const settlement = adjustment.enabled
    ? `The day-of adjustment configured at ${percent(adjustment.capFraction)} applies at `
      + `settlement, against a called event's actual pre-window load; a forward-looking estimate `
      + `has no event day to measure, so none is applied here.`
    : `The day-of adjustment is switched off in this configuration, so none applies at settlement `
      + `and none is applied here.`
  return 'The ten most recent non-event weekdays with comparable weather, averaged hour by hour. '
    + 'The day list is retained with the estimate, so a regulator can audit it. '
    + settlement
}

const NO_BASELINE = 'No comparable day survived selection for this premise, so there is no '
  + 'baseline and no figure derived from one. Nothing is fabricated in its place (R-2).'

/** What a failure that is not an `ApiError` — a bug in this package — says out loud. */
const LOAD_FAILED = 'That derivation could not be loaded.'

/** Appended to whatever went wrong, because closing and reopening really does retry. */
const RETRY_HINT = 'Close this row and open it again to try once more.'

/** `0.2` as `20%`. Percentages, not fractions, in anything a rep reads aloud. */
function percent(fraction: number, places = 0): string {
  return `${grouped(fraction * 100, places)}%`
}

const microLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

const bodyText: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.55,
  color: T.color.body,
  maxWidth: '88ch',
  margin: '0 0 12px',
}

const cell: CSSProperties = {
  textAlign: 'left',
  padding: '6px 12px 6px 0',
  borderBottom: `1px solid ${T.color.hairline}`,
  fontWeight: T.weight.body,
  fontSize: 12.5,
  verticalAlign: 'baseline',
}

const headCell: CSSProperties = { ...cell, ...microLabel, borderBottom: `1px solid ${T.color.rule}` }

const numberCell: CSSProperties = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

/** A `<dl>` of plain facts about a configuration or a method. */
function Facts({ rows, testId }: { rows: [string, ReactNode][]; testId: string }): JSX.Element {
  return (
    <dl
      data-testid={testId}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: '5px 14px',
        margin: 0,
        fontSize: 12.5,
        maxWidth: '60ch',
      }}
    >
      {rows.map(([term, value]) => (
        <Fact key={term} term={term}>{value}</Fact>
      ))}
    </dl>
  )
}

function Fact({ term, children }: { term: string; children: ReactNode }): JSX.Element {
  return (
    <>
      <dt style={{ color: T.color.micro }}>{term}</dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </>
  )
}

/**
 * The flags an aspect carries, when it carries any.
 *
 * Internal, like every flag in this system (display rule 7) — and the accordion
 * itself is an internal surface, which is why the derivation route has no
 * presentation variant at all.
 */
function AspectFlags({ flags }: { flags: EstimateFlag[] }): JSX.Element | null {
  if (flags.length === 0) return null
  return (
    <ul
      data-testid="derivation-flags"
      style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12.5, color: T.color.declinedInk }}
    >
      {flags.map((flag) => <li key={flag.code} data-flag-code={flag.code}>{flag.message}</li>)}
    </ul>
  )
}

interface PanelProps<A extends DerivationAspect> {
  payload: DerivationByAspect[A]
  confidence: ConfidenceLabel
  confidenceLabelId: string
}

/**
 * The ten days, as dates.
 *
 * A count would not be a derivation: "10 comparable days" is the claim, and the
 * list is the evidence for it. `excludePriorEventDays` and `excludeWeekends` are
 * stated beside them because a reader checking the dates against a calendar is
 * checking exactly those two rules.
 */
function BaselinePanel({ payload, confidence, confidenceLabelId }: PanelProps<'baseline'>): JSX.Element {
  const common: [string, ReactNode][] = [
    ['Method', payload.method],
    ['Lookback', `${grouped(payload.lookbackDays, 0)} days`],
    ['Weekends', payload.excludeWeekends ? 'Excluded' : 'Included'],
    ['Prior event days', payload.excludePriorEventDays ? 'Excluded' : 'Included'],
  ]

  if (!payload.available) {
    return (
      <>
        <p style={bodyText} data-testid="no-baseline">{NO_BASELINE}</p>
        <Facts rows={common} testId="baseline-facts" />
        <AspectFlags flags={payload.flags} />
      </>
    )
  }

  const { adjustment } = payload
  return (
    <>
      <p style={bodyText} data-testid="baseline-body">{baselineBody(adjustment)}</p>
      <Facts
        testId="baseline-facts"
        rows={[
          ...common,
          ['Peak', (
            <KwFigure
              kw={payload.peakKw}
              confidence={confidence}
              label="adjacent"
              confidenceLabelId={confidenceLabelId}
              testId="baseline-peak-kw"
            />
          )],
          ['Base load', (
            <KwFigure
              kw={payload.baseLoadKw}
              confidence={confidence}
              label="adjacent"
              confidenceLabelId={confidenceLabelId}
              testId="baseline-base-kw"
            />
          )],
          [
            'Day-of adjustment',
            <span data-testid="adjustment-factor">
              {adjustment.enabled
                // `×1.000`, not "none applied": the factor is what the stored
                // estimate carries, and the sentence above says what it means.
                ? `×${grouped(adjustment.factor, 3)} — within the configured `
                  + `${grouped(adjustment.lowerBound, 2)}–${grouped(adjustment.upperBound, 2)} band, `
                  + 'applied at settlement only'
                : 'Not enabled in this configuration'}
            </span>,
          ],
        ]}
      />
      <div style={{ ...microLabel, marginTop: 14 }}>Comparable days</div>
      <ol
        data-testid="comparable-days"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px 10px',
          listStyle: 'none',
          margin: '8px 0 0',
          padding: 0,
          fontSize: 12.5,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {payload.comparableDays.map((day) => (
          <li
            key={day}
            data-testid="comparable-day"
            data-day={day}
            style={{ border: `1px solid ${T.color.hairline}`, padding: '3px 8px', color: T.color.ink }}
          >
            {formatDate(day)}
          </li>
        ))}
      </ol>
      <AspectFlags flags={payload.flags} />
    </>
  )
}

/**
 * Which tier of the factor table each end use resolved at.
 *
 * **`industry_only` is not a deficiency and is not drawn as one** (display rules
 * 3 and 4): it is how an account nobody has characterized scores at all, and on
 * the shipped table it is the most specific tier any row can reach — no row in
 * it carries a building type. The column says which row governed; it does not
 * grade it.
 */
const BASIS_WORDS: Record<FactorsDerivation['basis'], string> = {
  industry_and_building_type: 'Industry and building type',
  industry_only: 'Industry row',
  default: 'Default row',
  none: 'No matching row',
}

function FactorsPanel({ payload, confidence, confidenceLabelId }: PanelProps<'factors'>): JSX.Element {
  const { key } = payload
  return (
    <>
      <p style={bodyText} data-testid="factors-body">{FACTORS_BODY}</p>
      <Facts
        testId="factors-key"
        rows={[
          ['Industry', `${key.naics} · ${key.naicsLabel}`],
          [
            'Building type',
            <span data-testid="factors-building-type">
              {key.buildingType === null
                ? (key.characterizationState === 'undetermined'
                  // Display rule 3's middle state. Somebody looked and could not
                  // tell, which is a different fact from nobody having looked,
                  // and neither is a gap in the lookup.
                  ? 'Undetermined — the industry row governs'
                  : 'Not characterized — the industry row governs')
                : `${asWords(key.buildingType)} · revision ${grouped(key.characterizationVersion ?? 0, 0)}`}
            </span>,
          ],
          ['Configuration', key.configVersionId],
        ]}
      />
      <table
        data-testid="factor-rows"
        style={{ borderCollapse: 'collapse', marginTop: 14, width: '100%', maxWidth: 620 }}
      >
        <thead>
          <tr>
            <th scope="col" style={headCell}>End use</th>
            <th scope="col" style={headCell}>Governing row</th>
            <th scope="col" style={{ ...headCell, textAlign: 'right' }}>Shed</th>
            <th scope="col" style={{ ...headCell, textAlign: 'right' }}>Shift</th>
            <th scope="col" style={{ ...headCell, textAlign: 'right' }}>Applied</th>
          </tr>
        </thead>
        <tbody>
          {payload.rows.map((row) => (
            <tr key={row.endUse} data-testid="factor-row" data-end-use={row.endUse} data-basis={row.basis}>
              <th scope="row" style={{ ...cell, color: T.color.ink }}>{asWords(row.endUse)}</th>
              <td style={cell}>{BASIS_WORDS[row.basis]}</td>
              <td style={numberCell}>{percent(row.shedFraction)}</td>
              <td style={numberCell}>{percent(row.shiftFraction)}</td>
              <td style={numberCell}>
                {/*
                  `null` is "the end-use mix never reached this one", which is
                  not the same as "it contributed zero" — a row can govern and
                  apply to nothing. Printing `0.0 kW` here would collapse the
                  two into the arithmetic one.
                */}
                {row.appliedKw === null
                  ? <span style={{ color: T.color.micro }} data-testid="factor-not-applied">Not in the mix</span>
                  : (
                    <KwFigure
                      kw={row.appliedKw}
                      confidence={confidence}
                      label="adjacent"
                      confidenceLabelId={confidenceLabelId}
                      testId={`factor-kw-${row.endUse}`}
                    />
                  )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

/**
 * Every hour of the sustained span, individually.
 *
 * Display rule 11 — *"the window is hourly, not aggregate. Resist any layout
 * that reduces the 5–10pm profile to a single figure"* — and this is the row
 * where the temptation is strongest, because the nomination *is* a single
 * figure. It is shown as the weakest of the hours listed beside it rather than
 * on its own.
 */
function SustainPanel({ payload, confidence, confidenceLabelId }: PanelProps<'sustain'>): JSX.Element {
  return (
    <>
      <p style={bodyText} data-testid="sustain-body">{SUSTAIN_BODY}</p>
      <Facts
        testId="sustain-facts"
        rows={[
          ['Event window', `${formatHour(payload.eventWindow.startHour)} – ${formatHour(payload.eventWindow.endHour)}`],
          ['Duration assumed', `${grouped(payload.assumedDurationHours, 0)} hours`],
          [
            'Nomination',
            <KwFigure
              kw={payload.nominationKw}
              confidence={confidence}
              label="adjacent"
              confidenceLabelId={confidenceLabelId}
              testId="sustain-nomination-kw"
            />,
          ],
          [
            'Binding hour',
            <span data-testid="sustain-binding">
              {/*
                A hand-entered nomination did not come from any hour, so it is
                not said to bind at one — the same ruling the drill-down's KPI
                caption and the chart's withheld `BINDS` marker are made under.
              */}
              {payload.overridden
                ? `Entered by hand — the weakest hour of the span is ${formatHour(payload.bindingHour)}`
                : formatHour(payload.bindingHour)}
            </span>,
          ],
        ]}
      />
      <table
        data-testid="sustain-hours"
        style={{ borderCollapse: 'collapse', marginTop: 14, maxWidth: 420, width: '100%' }}
      >
        <thead>
          <tr>
            <th scope="col" style={headCell}>Hour</th>
            <th scope="col" style={{ ...headCell, textAlign: 'right' }}>Curtailable</th>
            <th scope="col" style={headCell} />
          </tr>
        </thead>
        <tbody>
          {payload.hours.map((hour) => (
            <tr key={hour.hour} data-testid="sustain-hour" data-hour={hour.hour} data-binding={hour.binding}>
              <th scope="row" style={{ ...cell, color: T.color.ink }}>{formatHour(hour.hour)}</th>
              <td style={numberCell}>
                <KwFigure
                  kw={hour.kw}
                  confidence={confidence}
                  label="adjacent"
                  confidenceLabelId={confidenceLabelId}
                  testId={`sustain-kw-${hour.hour}`}
                />
              </td>
              <td style={cell}>
                {hour.binding && !payload.overridden
                  ? (
                    <span
                      data-testid="binding-marker"
                      style={{
                        ...microLabel,
                        color: '#fff',
                        background: T.color.navy,
                        borderRadius: T.radius.pill,
                        padding: '2px 9px',
                        fontWeight: T.weight.bold,
                      }}
                    >
                      Binds
                    </span>
                  )
                  : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <AspectFlags flags={payload.flags} />
    </>
  )
}

function ConfigPanel({ payload }: PanelProps<'config'>): JSX.Element {
  const { baseline, events, rates, season, eventWindow } = payload
  return (
    <>
      <p style={bodyText} data-testid="config-body">{configBody(payload.configVersionId)}</p>
      <Facts
        testId="config-facts"
        rows={[
          ['Revision', payload.configVersionId],
          ['Label', payload.label],
          ['Effective from', formatDate(payload.effectiveFrom)],
          ['Season', `${formatDate(season.start)} – ${formatDate(season.end)}`],
          ['Event window', `${formatHour(eventWindow.startHour)} – ${formatHour(eventWindow.endHour)}`],
          ['Events assumed', `${grouped(events.assumedCount, 0)} at ${grouped(events.assumedDurationHours, 0)} hours`],
          ['Energy rate', `${formatMoney(rates.energyPerKwh)} / kWh`],
          ['Capacity rate', `${formatMoney(rates.demandPerKwMonth)} / kW-month over ${grouped(rates.demandMonths, 1)} months`],
          ['Baseline method', baseline.method],
          [
            'Day-of adjustment',
            <span data-testid="config-adjustment">
              {/*
                Said the same way the baseline row says it, because a reader who
                opens both must not find two accounts of the same field. It is
                configured; it applies at settlement; it did not run here.
              */}
              {baseline.dayOfAdjustment.enabled
                ? `${percent(baseline.dayOfAdjustment.capFraction)} cap over the `
                  + `${grouped(baseline.dayOfAdjustment.windowHoursBefore, 0)} hours before the window, `
                  + 'at settlement only'
                : 'Not enabled'}
            </span>,
          ],
        ]}
      />
    </>
  )
}

type AspectLoad<A extends DerivationAspect> =
  | { phase: 'loading' }
  | { phase: 'failed'; message: string }
  | { phase: 'ready'; payload: DerivationByAspect[A] }

type Loads = { [A in DerivationAspect]?: AspectLoad<A> }

interface RowProps<A extends DerivationAspect> {
  aspect: A
  label: string
  open: boolean
  state: AspectLoad<A> | undefined
  onToggle: (aspect: A) => void
  value: (payload: DerivationByAspect[A]) => ReactNode
  children: (payload: DerivationByAspect[A]) => ReactNode
}

/**
 * One disclosure row.
 *
 * The panel is always in the document and carries `hidden` when closed, so
 * `aria-controls` always names an element that exists — a reference to a missing
 * id is worse than no reference, because assistive technology follows it and
 * finds nothing. Its *contents* are rendered only when open, which is what makes
 * "fetched on open" observable in the markup as well as on the wire.
 */
function Row<A extends DerivationAspect>(props: RowProps<A>): JSX.Element {
  const { aspect, label, open, state, onToggle } = props
  const buttonId = `derivation-button-${aspect}`
  const panelId = `derivation-panel-${aspect}`

  return (
    <div style={{ borderBottom: `1px solid ${T.color.hairline}` }}>
      <h3 style={{ margin: 0 }}>
        <button
          type="button"
          id={buttonId}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => { onToggle(aspect) }}
          data-testid={`derivation-row-${aspect}`}
          data-aspect={aspect}
          style={{
            width: '100%',
            textAlign: 'left',
            background: 'none',
            border: 0,
            padding: '12px 0',
            fontFamily: T.fontFamily,
            fontSize: 13.5,
            fontWeight: T.weight.body,
            color: T.color.ink,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            cursor: 'pointer',
          }}
        >
          <span>{label}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              data-testid={`derivation-value-${aspect}`}
              style={{ color: T.color.muted, fontSize: 12.5 }}
            >
              {state === undefined ? '' : state.phase === 'ready' ? props.value(state.payload)
                : state.phase === 'loading' ? 'Loading…' : 'Could not load'}
            </span>
            {/*
              The mock offers a closed row no affordance at all. `aria-hidden`
              because `aria-expanded` on the button already says this to a
              screen reader, and saying it twice is worse than saying it once.
            */}
            <span aria-hidden="true" style={{ color: T.color.chipOff, fontSize: 11 }}>
              {open ? '▾' : '▸'}
            </span>
          </span>
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!open}
        data-testid={`derivation-panel-${aspect}`}
        style={{ padding: '0 0 16px' }}
      >
        {!open || state === undefined ? null
          : state.phase === 'loading' ? <p style={bodyText}>Loading this derivation…</p>
            : state.phase === 'failed'
              ? (
                <p style={bodyText} data-testid="derivation-failed">
                  {`${state.message} ${RETRY_HINT}`}
                </p>
              )
              : props.children(state.payload)}
      </div>
    </div>
  )
}

export interface DerivationProps {
  accountId: string
  /**
   * **Display rule 1 reaches into here.** Every kW quantity below is a quantity
   * of the same estimate the drill-down's header labels, and a derivation panel
   * showing `227.2 kW` with the confidence pill scrolled off the top of the page
   * is the bare figure that rule refuses. The plan's interface for this
   * component is `<Derivation accountId />` alone; these two props are the
   * difference between honouring rule 1 and merely being near something that
   * does.
   */
  confidence: ConfidenceLabel
  /** The element carrying that label, for `KwFigure`'s `aria-describedby`. */
  confidenceLabelId: string
  /**
   * Whether a what-if is on the page. **These four rows always describe the
   * estimate of record**, and there is no route that can derive a scenario:
   * `GET /api/accounts/:id/derivation/:aspect` reads the stored estimate.
   *
   * So this is not a source switch, it is a truthfulness one. Task 16's finding
   * F-4: an open `Hours that must be sustained` panel reading 6pm–10pm and
   * 227.2 kW, left standing beside a scenario nomination of 179.6 kW, is a
   * panel a rep will read as the working behind the figure in front of them.
   * Flipping this closes every row, drops what was fetched, and says in the
   * heading which estimate the rows are the working for.
   */
  scenarioActive?: boolean
  /**
   * The same fact about the other way this page can stop showing the estimate
   * of record: a recompute for another day (`?asOf`, Task 8). The two are
   * mutually exclusive by construction — the drill-down disables the as-of
   * control under a scenario and the what-if control under a recompute — so at
   * most one of these is ever true.
   */
  asOfActive?: boolean
}

export function Derivation({
  accountId, confidence, confidenceLabelId, scenarioActive = false, asOfActive = false,
}: DerivationProps): JSX.Element {
  const [openAspect, setOpenAspect] = useState<DerivationAspect | null>(null)
  const [loads, setLoads] = useState<Loads>({})

  /**
   * Which aspects have been asked for, as a ref rather than as state.
   *
   * It guards the fetch, and a guard read out of `loads` would be read from the
   * render that produced the handler — two clicks inside one tick would both see
   * an empty map and both fire. Task 12's stale-props defect, which no test
   * caught because a human cannot click twice in one tick and `userEvent`
   * re-renders between clicks.
   */
  const requested = useRef<Set<DerivationAspect>>(new Set())
  const alive = useRef(true)
  /**
   * Bumped whenever this component is pointed at a different account, so a
   * request already in flight for the previous one cannot land its payload under
   * the new one's rows. Reachable: `App.tsx` renders one `AccountDetail` at one
   * position, so a `popstate` from one deep link to another changes this prop
   * rather than remounting anything.
   */
  const generation = useRef(0)

  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  useEffect(() => {
    generation.current += 1
    requested.current = new Set()
    setLoads({})
    setOpenAspect(null)
    // `scenarioActive` sits in here rather than in its own effect because it
    // wants exactly what an account change wants: every row closed, everything
    // fetched discarded, and any request in flight unable to land. What was
    // fetched is not *wrong* — it is the working behind the estimate of record,
    // which still exists — but it stops being the working behind the figures on
    // this page, and a panel is read as an explanation of what is beside it.
  }, [accountId, scenarioActive, asOfActive])

  const toggle = useCallback((aspect: DerivationAspect): void => {
    // Read from this render rather than from inside a `setState` updater. React
    // does not promise to invoke an updater synchronously — an earlier draft
    // decided "am I opening or closing" inside one and never fetched anything,
    // because the flag was still `false` when it was read. The single-valued
    // open row makes the closure read correct, and the `requested` ref below is
    // what a double click cannot get past.
    const opening = openAspect !== aspect
    setOpenAspect(opening ? aspect : null)
    if (!opening || requested.current.has(aspect)) return

    const era = generation.current
    const current = (): boolean => alive.current && generation.current === era
    requested.current.add(aspect)
    setLoads((prev) => ({ ...prev, [aspect]: { phase: 'loading' } }))
    void api.derivation(accountId, aspect)
      .then((payload) => {
        if (current()) setLoads((prev) => ({ ...prev, [aspect]: { phase: 'ready', payload } }))
      })
      .catch((error: unknown) => {
        if (!current()) return
        // Left retryable rather than swallowed: `requested` forgets the aspect,
        // so closing the row and opening it again asks the server again. A
        // failure that cannot be retried is a screen a rep has to reload.
        requested.current.delete(aspect)
        const message = error instanceof ApiError ? error.message : LOAD_FAILED
        setLoads((prev) => ({ ...prev, [aspect]: { phase: 'failed', message } }))
      })
  }, [accountId, openAspect])

  return (
    <section
      style={{ borderTop: `1.5px solid ${T.color.rule}`, paddingTop: 20, marginTop: 24 }}
      data-testid="derivation"
      data-of={scenarioActive ? 'record-under-scenario' : asOfActive ? 'record-under-recompute' : 'record'}
    >
      <h2 style={{ ...microLabel, margin: 0 }}>
        {scenarioActive ? SCENARIO_HEADING : asOfActive ? AS_OF_HEADING : HEADING}
      </h2>
      <div style={{ marginTop: 12 }} data-testid="derivation-rows">
        <Row
          aspect="baseline"
          label={BASELINE_LABEL}
          open={openAspect === 'baseline'}
          state={loads.baseline}
          onToggle={toggle}
          value={(payload: BaselineDerivation) => (payload.available
            ? (
              <>
                <KwFigure
                  kw={payload.peakKw}
                  confidence={confidence}
                  label="adjacent"
                  confidenceLabelId={confidenceLabelId}
                  testId="baseline-summary-kw"
                />
                {/*
                  The space is written out. JSX strips whitespace around a line
                  break, so `peak` on its own line renders — and reads back —
                  as `1,183.7 kWpeak`. Found by looking at the page, not by a
                  test: the assertion below was on the figure, which was right.
                */}
                {' peak'}
              </>
            )
            : 'No baseline')}
        >
          {(payload) => (
            <BaselinePanel payload={payload} confidence={confidence} confidenceLabelId={confidenceLabelId} />
          )}
        </Row>

        <Row
          aspect="factors"
          label={FACTORS_LABEL}
          open={openAspect === 'factors'}
          state={loads.factors}
          onToggle={toggle}
          // The mock's `short / btype`, with the mock's own fallback wording for
          // an account no building type is recorded for.
          value={(payload: FactorsDerivation) => `${payload.key.naicsLabel} / ${payload.key.buildingType === null
            ? 'industry row'
            : asWords(payload.key.buildingType)}`}
        >
          {(payload) => (
            <FactorsPanel payload={payload} confidence={confidence} confidenceLabelId={confidenceLabelId} />
          )}
        </Row>

        <Row
          aspect="sustain"
          label={SUSTAIN_LABEL}
          open={openAspect === 'sustain'}
          state={loads.sustain}
          onToggle={toggle}
          // The **span**, which is not the window: a 4-hour event inside a
          // 5-hour window is anchored at the window's close, so the first hour
          // of the window is not one of the hours that must be sustained.
          value={(payload: SustainDerivation) => (
            `${formatHour(payload.spanStartHour)} – ${formatHour(payload.spanEndHour)}`
          )}
        >
          {(payload) => (
            <SustainPanel payload={payload} confidence={confidence} confidenceLabelId={confidenceLabelId} />
          )}
        </Row>

        <Row
          aspect="config"
          label={CONFIG_LABEL}
          open={openAspect === 'config'}
          state={loads.config}
          onToggle={toggle}
          // The mock's literal `rev 7 · effective 1 Jun 2026`, replaced by the
          // version the estimate was actually stamped with.
          value={(payload: ConfigDerivation) => (
            `${payload.configVersionId} · effective ${formatDate(payload.effectiveFrom)}`
          )}
        >
          {(payload) => (
            <ConfigPanel payload={payload} confidence={confidence} confidenceLabelId={confidenceLabelId} />
          )}
        </Row>
      </div>
    </section>
  )
}
